// File: client_serena/src/components/PlanComparisonCarousel.js

import React from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Heading,
  Stack,
  useColorModeValue,
  Icon,
  Divider,
} from "@chakra-ui/react";
import { FaCheckCircle, FaUserMd, FaChartBar, FaUtensils, FaCrown, FaSmile } from "react-icons/fa";

function getElitePriceBreakdown(mealsPerDay = 3, daysInMonth = 30, avgCost = 2000, markup = 0.3) {
  const base = mealsPerDay * daysInMonth * avgCost;
  const total = Math.round(base * (1 + markup));
  return {
    base,
    markup: Math.round(base * markup),
    total,
  };
}

const accessTiers = [
  {
    key: "flex",
    title: "Flex (Pay as you go)",
    tagline: "Eat what you want, when you want.",
    price: "No monthly fee",
    benefits: [
      { icon: FaUtensils, text: "Pick meals anytime" },
      { icon: FaSmile, text: "No commitment, just good food" },
      { icon: FaChartBar, text: "Get a smart report with every order" },
    ],
    explain: "Perfect if you want to try things out or just order when you feel like it. Pay only for the meals you eat.",
    footnote: "You can change your meal budget anytime.",
    color: "#fef6e4",
  },
  {
    key: "smart",
    title: "Smart (₦5,000/mo)",
    tagline: "Unlimited plans, always learning.",
    price: "₦5,000 per month",
    benefits: [
      { icon: FaCheckCircle, text: "Unlimited meal plans" },
      { icon: FaChartBar, text: "See your progress every week" },
      { icon: FaUserMd, text: "1 free chat with a dietitian" },
    ],
    explain: "Great for students or anyone who wants to eat better and see their health improve, but doesn’t need meals delivered.",
    color: "#e4f9f5",
  },
  {
    key: "elite",
    title: "Elite (Full Subscription)",
    tagline: "All meals. All insights. All yours.",
    price: null, // Will be calculated
    benefits: [
      { icon: FaCrown, text: "All your meals delivered every week" },
      { icon: FaCheckCircle, text: "Unlimited support & shuffles" },
      { icon: FaUserMd, text: "Talk to a dietitian anytime" },
      { icon: FaChartBar, text: "See your health get better every month" },
    ],
    explain: "The easiest way to eat healthy, feel great, and never worry about what’s for dinner again.",
    footnote: "You can change your meal budget anytime. Price is based on your choices.",
    color: "#f0e9ff",
    isRecommended: true,
  },
];

function PlanComparisonCarousel({
  selected = "",
  onSelect = () => {},
  mealsPerDay = 3,
  budgetPlan = "Beta Plan",
  daysInMonth = 30,
}) {
  const borderColor = useColorModeValue("gray.300", "gray.600");
  // Example cost per plan
  const avgCostMap = {
    "Survival": 1000,
    "Beta Plan": 1600,
    "Chop Life Gang": 3500,
  };
  const avgCost = avgCostMap[budgetPlan] || 2000;
  const eliteBreakdown = getElitePriceBreakdown(mealsPerDay, daysInMonth, avgCost, 0.3);

  return (
    <Box mt={8}>
      <Heading size="md" mb={2}>Serena Plans: Find Your Fit</Heading>
      <Text fontSize="md" color="gray.700" mb={4}>
        We made our plans super easy. Just pick what matches your vibe, and you’re good to go!
      </Text>
      <Stack direction={["column", "row"]} spacing={6} mt={4}>
        {accessTiers.map((tier) => (
          <Box
            key={tier.key}
            bg={tier.color}
            borderRadius="md"
            borderWidth={selected === tier.key ? "2px" : "1px"}
            borderColor={selected === tier.key ? "teal.500" : borderColor}
            p={6}
            flex="1"
            minW="280px"
            shadow="sm"
            transition="all 0.2s"
            _hover={{ shadow: "md", cursor: "pointer" }}
            onClick={() => onSelect(tier.key)}
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            h="100%"
          >
            <HStack justify="space-between" mb={2}>
              <Heading size="md">{tier.title}</Heading>
              {tier.isRecommended && (
                <Badge colorScheme="green" fontSize="xs">
                  Most Popular
                </Badge>
              )}
            </HStack>
            <Text fontSize="lg" fontWeight="bold" mb={1}>{tier.tagline}</Text>
            <Text fontSize="md" color="gray.700" mb={2}>
              {tier.key === "elite" ? (
                <>
                  <b>How your price is set:</b><br />
                  <span style={{ fontSize: "1.1em" }}>
                    ₦{avgCost.toLocaleString()} per meal × {mealsPerDay} meals/day × {daysInMonth} days<br />
                    <br />
                    <b>= ₦{eliteBreakdown.total.toLocaleString()}/mo</b>
                  </span>
                </>
              ) : (
                tier.price
              )}
            </Text>
            <VStack align="start" spacing={2} fontSize="md" mb={2}>
              {tier.benefits.map((b, i) => (
                <HStack key={i}>
                  <Icon as={b.icon} color="teal.500" boxSize={5} />
                  <Text color="gray.800">{b.text}</Text>
                </HStack>
              ))}
            </VStack>
            <Divider my={2} />
            <Text fontSize="sm" color="gray.600" mb={2}>{tier.explain}</Text>
            {tier.footnote && (
              <Text fontSize="xs" color="gray.500" mt={1}>
                {tier.footnote}
              </Text>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export default PlanComparisonCarousel;
